import { ids } from "@icarros/theme-react";
import { hexToRgba } from "@icarros/utils-react";
import { Contrast } from "../../../types/general.types";
import styled, { css } from "styled-components";
const { color, easing, opacity, size } = ids || {};
const defaultStyle = css `
  background: transparent;
  border-color: ${color.action.neutral.ids_color_action_neutral_base};
  span {
    background: ${color.action.neutral.ids_color_action_neutral_base};
  }
`;
const defaultCheckedStyle = css `
  background: ${color.action.primary.ids_color_action_primary_base};
  border-color: ${color.action.primary.ids_color_action_primary_base};
  span {
    background: ${color.contrast.ids_color_contrast_onDark};
  }
`;
const disabledDefaultStyle = css `
  background: transparent;
  border-color: ${color.action.disabled.ids_color_action_disabled_base};
  span {
    background: ${color.action.disabled.ids_color_action_disabled_base};
  }
`;
const disabledDefaultCheckedStyle = css `
  background: ${color.action.disabled.ids_color_action_disabled_base};
  border-color: ${color.action.disabled.ids_color_action_disabled_base};
  span {
    background: ${color.contrast.ids_color_contrast_onDark};
  }
`;
const lightStyle = css `
  background: transparent;
  border-color: ${color.contrast.ids_color_contrast_onLight};
  span {
    background: ${color.contrast.ids_color_contrast_onLight};
  }
`;
const lightCheckedStyle = css `
  background: ${color.contrast.ids_color_contrast_onLight};
  border-color: ${color.contrast.ids_color_contrast_onLight};
  span {
    background: ${color.action.primary.ids_color_action_primary_base};
  }
`;
const disabledLightStyle = css `
  background: transparent;
  border-color: ${hexToRgba(color.contrast.ids_color_contrast_onLight, opacity.ids_opacity_medium)};
  span {
    background: ${hexToRgba(color.contrast.ids_color_contrast_onLight, opacity.ids_opacity_medium)};
  }
`;
const disabledLightCheckedStyle = css `
  background: ${hexToRgba(color.contrast.ids_color_contrast_onLight, opacity.ids_opacity_medium)};
  border-color: ${hexToRgba(color.contrast.ids_color_contrast_onLight, opacity.ids_opacity_empty)};
  span {
    background: ${hexToRgba(color.contrast.ids_color_contrast_onLight, opacity.ids_opacity_strong)};
  }
`;
const darkStyle = css `
  background: transparent;
  border-color: ${color.contrast.ids_color_contrast_onDark};
  span {
    background: ${color.contrast.ids_color_contrast_onDark};
  }
`;
const darkCheckedStyle = css `
  background: ${color.contrast.ids_color_contrast_onDark};
  border-color: ${color.contrast.ids_color_contrast_onDark};
  span {
    background: ${color.action.primary.ids_color_action_primary_base};
  }
`;
const disabledDarkStyle = css `
  background: transparent;
  border-color: ${hexToRgba(color.contrast.ids_color_contrast_onDark, opacity.ids_opacity_medium)};
  span {
    background: ${hexToRgba(color.contrast.ids_color_contrast_onDark, opacity.ids_opacity_medium)};
  }
`;
const disabledDarkCheckedStyle = css `
  background: ${hexToRgba(color.contrast.ids_color_contrast_onDark, opacity.ids_opacity_medium)};
  border-color: ${hexToRgba(color.contrast.ids_color_contrast_onDark, opacity.ids_opacity_empty)};
  span {
    background: ${hexToRgba(color.contrast.ids_color_contrast_onDark, opacity.ids_opacity_strong)};
  }
`;
const getDefaultStyle = (isChecked, isDisabled) => {
    if (isChecked) {
        return isDisabled ? disabledDefaultCheckedStyle : defaultCheckedStyle;
    }
    return isDisabled ? disabledDefaultStyle : defaultStyle;
};
const getLightStyle = (isChecked, isDisabled) => {
    if (isChecked) {
        return isDisabled ? disabledLightCheckedStyle : lightCheckedStyle;
    }
    return isDisabled ? disabledLightStyle : lightStyle;
};
const getDarkStyle = (isChecked, isDisabled) => {
    if (isChecked) {
        return isDisabled ? disabledDarkCheckedStyle : darkCheckedStyle;
    }
    return isDisabled ? disabledDarkStyle : darkStyle;
};
const getSwitchStyle = (contrast, isChecked, isDisabled) => {
    switch (contrast) {
        case Contrast.light:
            return getLightStyle(isChecked, isDisabled);
        case Contrast.dark:
            return getDarkStyle(isChecked, isDisabled);
        default:
            return getDefaultStyle(isChecked, isDisabled);
    }
};
export const StyledInput = styled.input `
  position: absolute;
  opacity: 0;
`;
export const SwitchWrapper = styled.div `
  height: ${size.general.ids_size_general_48x};
  width: ${size.general.ids_size_general_48x};
  background: ${opacity.ids_opacity_empty};
  display: flex;
  justify-content: center;
  align-items: center;
`;
export const StyledSwitch = styled.label `
  position: relative;
  display: block;
  height: ${size.general.ids_size_general_20x};
  width: ${size.general.ids_size_general_36x};
  border: ${size.border_width.ids_size_border_small} solid
    ${color.action.neutral.ids_color_action_neutral_base};
  border-radius: ${size.general.ids_size_general_10x};
  transition-duration: ${ids.time.productive.fast
    .ids_time_productive_fast_input};
  transition-timing-function: ${({ isChecked }) => isChecked
    ? easing.expressive.out.ids_easing_expressive_decelerate
    : easing.productive.in.ids_easing_productive_accelerate_01};
  ${({ contrast, isChecked, isDisabled }) => getSwitchStyle(contrast, isChecked, isDisabled)};
  cursor: pointer;
  input:checked + span {
    left: calc(
      100% - ${size.general.ids_size_general_16x} -
        ${size.general.ids_size_general_1x}
    );
  }
`;
export const SwitchDot = styled.span `
  width: ${size.general.ids_size_general_14x};
  height: ${size.general.ids_size_general_14x};
  padding: ${size.general.ids_size_general_3x};
  border-radius: ${size.general.ids_size_general_48x};
  display: block;
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  left: ${size.general.ids_size_general_3x};
  transition-duration: ${ids.time.productive.fast
    .ids_time_productive_fast_input};
  transition-timing-function: ${({ isChecked }) => isChecked
    ? easing.expressive.out.ids_easing_expressive_decelerate
    : easing.productive.in.ids_easing_productive_accelerate_01};
`;
//# sourceMappingURL=Switch.style.js.map